import React, { useState, useEffect } from 'react';
import { Bell, X, Monitor, Smartphone, Check, Volume2, Vibrate } from 'lucide-react';
import { requestNotificationPermission, getNotificationStatus } from '../services/PushNotificationService';

function NotificationPermissionModal({ isOpen, onClose, userId }) {
    const [activeTab, setActiveTab] = useState('desktop');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(getNotificationStatus());
    const [isMobile, setIsMobile] = useState(false);

    // 모바일 감지
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 480);
            // 모바일이면 모바일 탭으로 자동 전환
            if (window.innerWidth <= 480) {
                setActiveTab('mobile');
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    if (!isOpen) return null;

    const handleAllow = async () => {
        setLoading(true);
        try {
            const result = await requestNotificationPermission(userId);
            if (result.success) {
                setStatus('granted');
                // 모바일에서 진동 피드백
                if ('vibrate' in navigator) {
                    navigator.vibrate([100, 50, 100]);
                }
                setTimeout(() => onClose(), 1500);
            } else {
                setStatus(getNotificationStatus());
            }
        } catch (error) {
            console.error('알림 허용 처리 실패:', error);
            setStatus(getNotificationStatus());
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={{...styles.modal, ...(isMobile ? styles.modalMobile : {})}}>
                <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>

                <div style={{...styles.header, ...(isMobile ? styles.headerMobile : {})}}>
                    <div style={styles.headerIconWrapper}>
                        <Bell size={isMobile ? 28 : 24} />
                    </div>
                    <h2 style={{...styles.headerTitle, ...(isMobile ? styles.headerTitleMobile : {})}}>
                        {isMobile ? '알림 받기' : '브라우저 푸시 알림'}
                    </h2>
                    <p style={{...styles.headerDesc, ...(isMobile ? styles.headerDescMobile : {})}}>
                        {isMobile
                            ? '중요한 메시지를 놓치지 마세요'
                            : '다른 작업 중에도 채팅 알림을 받을 수 있어요'}
                    </p>
                </div>

                <div style={{...styles.body, ...(isMobile ? styles.bodyMobile : {})}}>
                    {/* 탭 - 모바일에서는 숨김 */}
                    {!isMobile && (
                        <div style={styles.tabs}>
                            <button
                                style={{...styles.tab, ...(activeTab === 'desktop' ? styles.tabActive : {})}}
                                onClick={() => setActiveTab('desktop')}
                            >
                                <Monitor size={16} /> 데스크톱
                            </button>
                            <button
                                style={{...styles.tab, ...(activeTab === 'mobile' ? styles.tabActive : {})}}
                                onClick={() => setActiveTab('mobile')}
                            >
                                <Smartphone size={16} /> 모바일
                            </button>
                        </div>
                    )}

                    {/* 데스크톱 시뮬레이션 */}
                    {activeTab === 'desktop' && !isMobile && (
                        <div style={styles.demoContainer}>
                            <div style={styles.desktopSim}>
                                <div style={styles.notificationCard}>
                                    <div style={styles.notifHeader}>
                                        <div style={styles.notifIcon}>🦷</div>
                                        <span style={styles.notifApp}>DentalCluster</span>
                                        <span style={styles.notifTime}>지금</span>
                                    </div>
                                    <div style={styles.notifTitle}>새 채팅 메시지</div>
                                    <div style={styles.notifBody}>서울미소치과: 김철수 환자 건 확인 부탁드립니다.</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 모바일 시뮬레이션 */}
                    {(activeTab === 'mobile' || isMobile) && (
                        <div style={{...styles.demoContainer, ...(isMobile ? styles.demoContainerMobile : {})}}>
                            <div style={{...styles.mobileSim, ...(isMobile ? styles.mobileSimCompact : {})}}>
                                <div style={styles.mobileNotch}></div>
                                <div style={styles.mobileNotification}>
                                    <div style={styles.notifHeader}>
                                        <div style={{...styles.notifIcon, width: 26, height: 26, fontSize: 13}}>🦷</div>
                                        <span style={{...styles.notifApp, fontSize: 11}}>DentalCluster</span>
                                        <span style={{...styles.notifTime, fontSize: 10}}>지금</span>
                                    </div>
                                    <div style={{...styles.notifTitle, fontSize: 12}}>새 채팅 메시지</div>
                                    <div style={{...styles.notifBody, fontSize: 11}}>서울미소치과: 김철수 환자...</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 기능 안내 */}
                    <div style={{...styles.features, ...(isMobile ? styles.featuresMobile : {})}}>
                        {!isMobile && <h4 style={styles.featuresTitle}>이런 점이 좋아요</h4>}
                        <ul style={{...styles.featuresList, ...(isMobile ? styles.featuresListMobile : {})}}>
                            <li style={{...styles.featureItem, ...(isMobile ? styles.featureItemMobile : {})}}>
                                <Check size={isMobile ? 18 : 16} color="#10b981" />
                                <span>다른 앱/탭을 보고 있어도 알림이 와요</span>
                            </li>
                            <li style={{...styles.featureItem, ...(isMobile ? styles.featureItemMobile : {})}}>
                                <Volume2 size={isMobile ? 18 : 16} color="#10b981" />
                                <span>알림 소리로 바로 확인 가능</span>
                            </li>
                            {isMobile && (
                                <li style={{...styles.featureItem, ...styles.featureItemMobile}}>
                                    <Vibrate size={18} color="#10b981" />
                                    <span>진동으로 알려드려요</span>
                                </li>
                            )}
                            <li style={{...styles.featureItem, ...(isMobile ? styles.featureItemMobile : {})}}>
                                <Check size={isMobile ? 18 : 16} color="#10b981" />
                                <span>언제든 설정에서 끌 수 있어요</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div style={{...styles.footer, ...(isMobile ? styles.footerMobile : {})}}>
                    {status === 'granted' ? (
                        <div style={{...styles.successMsg, ...(isMobile ? styles.successMsgMobile : {})}}>
                            <Check size={20} /> 알림이 활성화되었습니다!
                        </div>
                    ) : status === 'denied' ? (
                        <div style={{...styles.deniedMsg, ...(isMobile ? styles.deniedMsgMobile : {})}}>
                            {isMobile
                                ? '브라우저 설정에서 알림을 허용해주세요'
                                : '브라우저 설정에서 알림을 허용해주세요'}
                        </div>
                    ) : (
                        <div style={{...styles.buttonGroup, ...(isMobile ? styles.buttonGroupMobile : {})}}>
                            <button
                                style={{...styles.btnSecondary, ...(isMobile ? styles.btnSecondaryMobile : {})}}
                                onClick={onClose}
                            >
                                나중에
                            </button>
                            <button
                                style={{...styles.btnPrimary, ...(isMobile ? styles.btnPrimaryMobile : {})}}
                                onClick={handleAllow}
                                disabled={loading}
                            >
                                {loading ? '처리 중...' : (isMobile ? '알림 허용' : '알림 허용하기')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 10000 },
    modal: { background: 'white', borderRadius: 20, maxWidth: 420, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)', position: 'relative' },
    modalMobile: { borderRadius: 24, margin: 12, maxWidth: 'none', width: 'calc(100% - 24px)' },
    closeBtn: { position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b', zIndex: 1 },
    header: { background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', padding: 24, borderRadius: '20px 20px 0 0', textAlign: 'center', color: 'white' },
    headerMobile: { padding: '32px 20px', borderRadius: '24px 24px 0 0' },
    headerIconWrapper: { width: 56, height: 56, background: 'rgba(255,255,255,0.2)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' },
    headerTitle: { fontSize: 20, margin: 0, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 },
    headerTitleMobile: { fontSize: 22, marginBottom: 6 },
    headerDesc: { fontSize: 14, margin: 0, opacity: 0.9 },
    headerDescMobile: { fontSize: 15 },
    body: { padding: 20 },
    bodyMobile: { padding: 16 },
    tabs: { display: 'flex', gap: 8, marginBottom: 16 },
    tab: { flex: 1, padding: 12, border: 'none', background: '#f1f5f9', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
    tabActive: { background: '#6366f1', color: 'white' },
    demoContainer: { marginBottom: 16 },
    demoContainerMobile: { marginBottom: 12 },
    desktopSim: { background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: 12, padding: 16, position: 'relative', minHeight: 160 },
    notificationCard: { position: 'absolute', top: 16, right: 12, width: 240, background: 'rgba(255, 255, 255, 0.97)', borderRadius: 12, padding: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    notifHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
    notifIcon: { width: 28, height: 28, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 },
    notifApp: { fontSize: 12, fontWeight: 600, color: '#1e293b' },
    notifTime: { fontSize: 11, color: '#94a3b8', marginLeft: 'auto' },
    notifTitle: { fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 2 },
    notifBody: { fontSize: 12, color: '#64748b', lineHeight: 1.4 },
    mobileSim: { width: 180, height: 280, background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: 28, padding: 12, margin: '0 auto', position: 'relative', border: '5px solid #1e293b' },
    mobileSimCompact: { width: '100%', height: 'auto', minHeight: 120, borderRadius: 16, padding: 10, border: '3px solid #1e293b' },
    mobileNotch: { width: 70, height: 20, background: '#1e293b', borderRadius: '0 0 12px 12px', position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' },
    mobileNotification: { background: 'rgba(255, 255, 255, 0.97)', borderRadius: 12, padding: 10, marginTop: 28, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
    features: { background: '#f8fafc', borderRadius: 12, padding: 16 },
    featuresMobile: { background: 'transparent', padding: 0 },
    featuresTitle: { fontSize: 14, color: '#1e293b', margin: '0 0 12px 0' },
    featuresList: { listStyle: 'none', margin: 0, padding: 0 },
    featuresListMobile: { display: 'flex', flexDirection: 'column', gap: 8 },
    featureItem: { fontSize: 13, color: '#64748b', padding: '8px 0', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #e2e8f0' },
    featureItemMobile: { padding: '10px 14px', background: '#f8fafc', borderRadius: 10, borderBottom: 'none', fontSize: 14 },
    footer: { padding: '16px 20px 20px', display: 'flex', gap: 12 },
    footerMobile: { padding: '12px 16px 24px', flexDirection: 'column' },
    buttonGroup: { display: 'flex', gap: 12, flex: 1 },
    buttonGroupMobile: { flexDirection: 'column-reverse', gap: 10 },
    btnSecondary: { flex: 1, padding: '14px 20px', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#64748b' },
    btnSecondaryMobile: { padding: '16px 20px', fontSize: 16 },
    btnPrimary: { flex: 1, padding: '14px 20px', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: 'white' },
    btnPrimaryMobile: { padding: '18px 20px', fontSize: 17, borderRadius: 14 },
    successMsg: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, background: '#dcfce7', color: '#166534', borderRadius: 12, fontWeight: 600 },
    successMsgMobile: { padding: 18, fontSize: 16 },
    deniedMsg: { flex: 1, textAlign: 'center', padding: 14, background: '#fef2f2', color: '#991b1b', borderRadius: 12, fontSize: 13 },
    deniedMsgMobile: { padding: 16, fontSize: 14 },
};

export default NotificationPermissionModal;
